import { createTheme } from "@mui/material/styles";
import { BookingPaletteTokens, AppThemeTokens } from "./themeTypes";

declare module "@mui/material/styles" {
  interface Palette {
    booking: BookingPaletteTokens;
    app: {
      primarySoft: string;
      surface: string;
      surfaceSoft: string;
      surfaceBlue: string;
      border: string;
      borderSoft: string;
      textMuted: string;
      dangerSoft: string;
    };
  }

  interface PaletteOptions {
    booking?: BookingPaletteTokens;
    app?: {
      primarySoft: string;
      surface: string;
      surfaceSoft: string;
      surfaceBlue: string;
      border: string;
      borderSoft: string;
      textMuted: string;
      dangerSoft: string;
    };
  }
}

export const createAppTheme = ({
  tokens,
  fontScale,
  mode,
}: {
  tokens: AppThemeTokens;
  fontScale: number;
  mode: "light" | "dark";
}) => {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.primary,
      },
      background: {
        default: tokens.background,
        paper: tokens.surface,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      divider: tokens.border,
      error: {
        main: tokens.danger,
      },
      success: {
        main: tokens.booking.paid,
      },
      warning: {
        main: tokens.booking.deposit,
      },
      booking: tokens.booking,
      app: {
        primarySoft: tokens.primarySoft,
        surface: tokens.surface,
        surfaceSoft: tokens.surfaceSoft,
        surfaceBlue: tokens.surfaceBlue,
        border: tokens.border,
        borderSoft: tokens.borderSoft,
        textMuted: tokens.textMuted,
        dangerSoft: tokens.dangerSoft,
      },
    },
    shape: {
      borderRadius: 4,
    },
    typography: {
      htmlFontSize: 16,
      fontSize: 14 + fontScale,
      fontFamily:
        "Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
      h1: {
        fontWeight: 950,
        letterSpacing: "-0.03em",
      },
      h2: {
        fontWeight: 950,
        letterSpacing: "-0.03em",
      },
      h3: {
        fontWeight: 950,
        letterSpacing: "-0.025em",
      },
      h4: {
        fontWeight: 950,
        letterSpacing: "-0.025em",
      },
      h5: {
        fontWeight: 950,
        letterSpacing: "-0.018em",
      },
      h6: {
        fontWeight: 900,
        letterSpacing: "-0.01em",
      },
      button: {
        fontWeight: 900,
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            minHeight: "100%",
            backgroundColor: tokens.background,
          },
          body: {
            minHeight: "100%",
            backgroundColor: tokens.background,
            color: tokens.textPrimary,
            transition:
              "background-color 180ms ease, color 180ms ease, font-size 180ms ease",
          },
          "#root": {
            minHeight: "100%",
            backgroundColor: tokens.background,
          },
          "*": {
            boxSizing: "border-box",
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            color: tokens.textPrimary,
          },
          rounded: {
            borderRadius: 16,
          },
        },
      },

      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            position: "relative",
            overflow: "hidden",
            borderRadius: 16,
            backgroundImage: "none",
            backgroundColor: tokens.surface,
            color: tokens.textPrimary,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowCard,
            transition:
              "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease, color 160ms ease",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: tokens.primary,
              opacity: 0.95,
              zIndex: 1,
            },
            "& > *": {
              position: "relative",
              zIndex: 2,
            },
            "&.rp-card-no-accent::before": {
              display: "none",
            },
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 16,
            "&:last-child": {
              paddingBottom: 16,
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            backgroundImage: "none",
            backgroundColor: tokens.surface,
            color: tokens.textPrimary,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowPanel,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.surface,
            color: tokens.textPrimary,
            borderColor: tokens.border,
            backgroundImage: "none",
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },

      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 999,
            fontWeight: 900,
            minHeight: 36,
            boxShadow: "none",
          },
          contained: {
            color: tokens.textOnPrimary,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
          outlined: {
            backgroundColor: tokens.surface,
            borderColor: tokens.border,
            "&:hover": {
              backgroundColor: tokens.surfaceSoft,
              borderColor: tokens.primary,
            },
          },
          text: {
            "&:hover": {
              backgroundColor: tokens.surfaceSoft,
            },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            color: tokens.textSecondary,
            transition:
              "background-color 160ms ease, color 160ms ease, border-color 160ms ease",
            "&:hover": {
              backgroundColor: tokens.surfaceSoft,
              color: tokens.primary,
            },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 900,
          },
          sizeSmall: {
            height: 26,
            fontSize: 12,
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: tokens.surface,
            color: tokens.textPrimary,
            transition:
              "border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.primary,
              borderWidth: 1,
            },
          },
          notchedOutline: {
            borderColor: tokens.border,
          },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            color: tokens.textPrimary,
          },
          input: {
            color: tokens.textPrimary,
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tokens.textSecondary,
            fontWeight: 750,
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            backgroundImage: "none",
            backgroundColor: tokens.surface,
            color: tokens.textPrimary,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowFloating,
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: tokens.textPrimary,
            borderRadius: 8,
            margin: "2px 6px",
            fontWeight: 750,
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: tokens.textPrimary,
            borderRadius: 10,
            "&.Mui-selected": {
              backgroundColor: tokens.primarySoft,
              color: tokens.primary,
            },
            "&.Mui-selected:hover": {
              backgroundColor: tokens.primarySoft,
            },
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.border,
          },
        },
      },

      MuiTypography: {
        styleOverrides: {
          root: {
            transition: "font-size 180ms ease, color 180ms ease",
          },
        },
      },

      MuiAccordion: {
        defaultProps: {
          elevation: 0,
          disableGutters: true,
        },
        styleOverrides: {
          root: {
            borderRadius: "14px !important",
            border: `1px solid ${tokens.border}`,
            backgroundImage: "none",
            backgroundColor: tokens.surface,
            boxShadow: tokens.shadowCard,
            overflow: "hidden",
            "&:before": {
              display: "none",
            },
          },
        },
      },

      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 54,
            paddingLeft: 18,
            paddingRight: 18,
          },
          content: {
            margin: "12px 0",
          },
        },
      },

      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: "0 18px 18px",
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${tokens.border}`,
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontWeight: 800,
          },
        },
      },
    },
  });
};
